import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FiCalendar, FiCheck, FiChevronLeft, FiChevronRight, FiClock, FiPlus,
  FiTrash2, FiX, FiPlay, FiToggleLeft, FiToggleRight, FiEdit2, FiList,
} from 'react-icons/fi';
import DurationPicker, { formatDurationLabel } from '../DurationPicker';

function authHeaders() {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
}

function displayName(m) {
  if (!m) return '';
  return String(m.fio || m.username || m.assignee_fio || m.assignee_username || `ID ${m.user_id || m.id || ''}`).trim();
}

const WEEKDAYS = [
  { v: 1, label: 'Пн' },
  { v: 2, label: 'Вт' },
  { v: 3, label: 'Ср' },
  { v: 4, label: 'Чт' },
  { v: 5, label: 'Пт' },
  { v: 6, label: 'Сб' },
  { v: 0, label: 'Вс' },
];

const MONTH_DAY_NUMS = Array.from({ length: 31 }, (_, i) => i + 1);

const REC_OPTIONS = [
  { v: 'none', label: 'Без автозапуска' },
  { v: 'once', label: 'Один раз (дата и время)' },
  { v: 'interval', label: 'Каждые N дней' },
  { v: 'daily', label: 'Каждый день' },
  { v: 'weekdays', label: 'По будням (пн–пт)' },
  { v: 'weekends', label: 'По выходным (сб–вс)' },
  { v: 'weekly', label: 'Раз в неделю' },
  { v: 'monthdays', label: 'Несколько дней месяца' },
];

const WEEKDAY_SHORT = { 0: 'Вс', 1: 'Пн', 2: 'Вт', 3: 'Ср', 4: 'Чт', 5: 'Пт', 6: 'Сб' };

function parseDaysList(raw) {
  if (Array.isArray(raw)) return raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    } catch (_) {}
  }
  return [];
}

function recLabel(rec, intervalDays, monthDays, weekdays) {
  if (rec === 'interval') {
    const n = Number(intervalDays) || 0;
    return n > 0 ? `Каждые ${n} дн.` : 'Каждые N дней';
  }
  if (rec === 'monthdays') {
    const days = parseDaysList(monthDays);
    return days.length ? `Дни месяца: ${days.join(', ')}` : 'Несколько дней месяца';
  }
  if (rec === 'weekly') {
    const days = parseDaysList(weekdays);
    if (days.length > 1) return days.map((d) => WEEKDAY_SHORT[d] || d).join(', ');
  }
  return REC_OPTIONS.find((o) => o.v === rec)?.label || rec || '—';
}

function monthLabel(y, m) {
  const d = new Date(y, m, 1);
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const Overlay = styled.div`
  position: absolute; inset: 0; z-index: 40;
  display: flex; align-items: stretch; justify-content: stretch;
  background: rgba(8, 12, 22, 0.55);
  backdrop-filter: blur(6px);
  padding: 0;
`;

const Panel = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  border-radius: 0;
  background: ${({ $light }) => ($light ? '#f7f9fc' : '#0f172a')};
  color: ${({ $light }) => ($light ? '#17233b' : '#eaf2fb')};
  border: none;
  box-shadow: none;
  display: flex; flex-direction: column;
`;

const Head = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.08)' : 'rgba(255,255,255,.08)')};
  flex-shrink: 0;
  background: inherit;
  h3 { margin: 0; font-size: 1.05rem; font-weight: 800; }
  button.close {
    border: 0; background: transparent; color: inherit; cursor: pointer;
    width: 36px; height: 36px; border-radius: 10px; margin-left: auto;
    display: grid; place-items: center;
    &:hover { background: rgba(56,182,255,.12); }
  }
`;

const Tabs = styled.div`
  display: flex; gap: 6px; padding: 10px 18px 0;
  flex-shrink: 0;
`;

const TabBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px;
  border: 0;
  border-radius: 12px 12px 0 0;
  font-weight: 800;
  font-size: .9rem;
  cursor: pointer;
  color: ${({ $on }) => ($on ? '#38b6ff' : 'inherit')};
  background: ${({ $on, $light }) => {
    if (!$on) return 'transparent';
    return $light ? '#fff' : 'rgba(255,255,255,.05)';
  }};
  border-bottom: 2px solid ${({ $on }) => ($on ? '#38b6ff' : 'transparent')};
  opacity: ${({ $on }) => ($on ? 1 : 0.65)};
  &:hover { opacity: 1; background: rgba(56,182,255,.08); }
  .badge {
    min-width: 22px; height: 22px; padding: 0 6px;
    border-radius: 999px;
    display: inline-grid; place-items: center;
    font-size: .72rem; font-weight: 900;
    background: rgba(56,182,255,.2); color: #38b6ff;
  }
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 18px 20px;
`;

const CreateLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  border-radius: 16px;
  padding: 14px;
  background: ${({ $light }) => ($light ? '#fff' : 'rgba(255,255,255,.04)')};
  border: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.08)' : 'rgba(255,255,255,.08)')};
`;

const CalWrap = styled.div`
  width: 100%;
  max-width: 320px;
`;

const CalHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 800; text-transform: capitalize;
  font-size: .92rem;
  button {
    border: 0; background: rgba(56,182,255,.12); color: #38b6ff;
    width: 28px; height: 28px; border-radius: 8px; cursor: pointer;
    display: grid; place-items: center;
  }
`;

const Dow = styled.div`
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; margin-bottom: 3px;
  span { text-align: center; font-size: .68rem; opacity: .55; font-weight: 700; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px;
`;

const DayCell = styled.button`
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ $picked }) => ($picked ? 'rgba(251,191,36,.85)' : 'transparent')};
  background: ${({ $busy, $selected, $picked, $light, $today }) => {
    if ($picked) return $light ? 'rgba(251,191,36,.35)' : 'rgba(251,191,36,.28)';
    if ($selected) return 'linear-gradient(135deg,#38b6ff,#2488d8)';
    if ($busy) return $light ? 'rgba(56,182,255,.18)' : 'rgba(56,182,255,.22)';
    if ($today) return $light ? 'rgba(23,35,59,.06)' : 'rgba(255,255,255,.06)';
    return 'transparent';
  }};
  color: ${({ $selected, $picked, $light }) => {
    if ($selected) return '#fff';
    if ($picked) return $light ? '#92400e' : '#fde68a';
    return 'inherit';
  }};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};
  font-weight: ${({ $busy, $picked }) => ($busy || $picked ? 800 : 600)};
  font-size: .78rem;
  position: relative;
  &::after {
    content: '';
    display: ${({ $busy, $selected, $picked }) => ($busy && !$selected && !$picked ? 'block' : 'none')};
    position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%);
    width: 4px; height: 4px; border-radius: 50%;
    background: #38b6ff;
  }
`;

const Field = styled.label`
  display: flex; flex-direction: column; gap: 4px;
  margin-bottom: 10px;
  font-size: .78rem; font-weight: 700; opacity: .75;
  input, select, textarea {
    font: inherit; font-weight: 600; opacity: 1;
    border-radius: 10px; padding: 9px 10px;
    border: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.12)' : 'rgba(255,255,255,.12)')};
    background: ${({ $light }) => ($light ? '#f4f7fb' : 'rgba(0,0,0,.25)')};
    color: inherit;
  }
  textarea { min-height: 64px; resize: vertical; }
`;

const RecRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;
  button {
    border-radius: 999px; padding: 6px 10px; font-size: .78rem; font-weight: 700;
    cursor: pointer;
    border: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.12)' : 'rgba(255,255,255,.12)')};
    background: transparent; color: inherit;
    &.on {
      background: rgba(56,182,255,.2);
      border-color: rgba(56,182,255,.45);
      color: #38b6ff;
    }
  }
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: 0; border-radius: 10px; padding: 9px 12px;
  font-weight: 800; cursor: pointer;
  background: ${({ $ghost, $danger }) => {
    if ($danger) return 'rgba(239,68,68,.18)';
    if ($ghost) return 'rgba(56,182,255,.12)';
    return 'linear-gradient(135deg,#38b6ff,#2488d8)';
  }};
  color: ${({ $ghost, $danger }) => ($danger ? '#fecaca' : $ghost ? '#38b6ff' : '#fff')};
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const DayDetail = styled.div`
  margin-top: 10px; padding-top: 10px;
  border-top: 1px solid ${({ $light }) => ($light ? 'rgba(23,35,59,.08)' : 'rgba(255,255,255,.08)')};
  font-size: .85rem;
  ul { margin: 6px 0 0; padding-left: 18px; }
  li { margin-bottom: 4px; }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  width: 100%;
`;

const TplCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 18px;
  padding: 18px 18px 16px;
  min-height: 190px;
  overflow: hidden;
  border: 1.5px solid ${({ $light, $enabled, $on }) => {
    if ($on) return 'rgba(56,182,255,.65)';
    if ($enabled) return $light ? 'rgba(34,160,107,.35)' : 'rgba(34,197,94,.4)';
    return $light ? 'rgba(23,35,59,.14)' : 'rgba(255,255,255,.12)';
  }};
  background: ${({ $light, $enabled, $on }) => {
    if ($on) {
      return $light
        ? 'linear-gradient(145deg, #e8f6ff 0%, #f7fbff 55%, #fff 100%)'
        : 'linear-gradient(145deg, rgba(56,182,255,.18) 0%, rgba(15,23,42,.9) 100%)';
    }
    if ($enabled) {
      return $light
        ? 'linear-gradient(145deg, #ecfdf5 0%, #f0fdf8 40%, #ffffff 100%)'
        : 'linear-gradient(145deg, rgba(34,197,94,.14) 0%, rgba(15,23,42,.92) 100%)';
    }
    return $light
      ? 'linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,.05) 0%, rgba(15,23,42,.95) 100%)';
  }};
  box-shadow: ${({ $light }) => (
    $light
      ? '0 8px 24px rgba(23,35,59,.08), 0 1px 0 rgba(255,255,255,.8) inset'
      : '0 10px 28px rgba(0,0,0,.28)'
  )};
  transition: transform .15s ease, border-color .15s ease, box-shadow .15s ease;
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 5px;
    background: ${({ $enabled, $on }) => {
      if ($on) return 'linear-gradient(180deg,#38b6ff,#2488d8)';
      if ($enabled) return 'linear-gradient(180deg,#22c55e,#16a34a)';
      return 'linear-gradient(180deg,#94a3b8,#64748b)';
    }};
  }
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ $light }) => (
      $light
        ? '0 14px 32px rgba(23,35,59,.12)'
        : '0 16px 36px rgba(0,0,0,.4)'
    )};
  }
  .title {
    font-size: 1.12rem;
    font-weight: 900;
    line-height: 1.3;
    margin: 0;
    color: ${({ $light }) => ($light ? '#0f172a' : '#f8fafc')};
    padding-left: 6px;
  }
  .meta {
    display: flex; flex-wrap: wrap; gap: 6px;
    padding-left: 6px;
  }
  .chip {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: .74rem;
    font-weight: 800;
    background: ${({ $light }) => ($light ? 'rgba(37,99,235,.1)' : 'rgba(56,182,255,.16)')};
    color: ${({ $light }) => ($light ? '#1d4ed8' : '#7dd3fc')};
    border: 1px solid ${({ $light }) => ($light ? 'rgba(37,99,235,.22)' : 'rgba(56,182,255,.3)')};
  }
  .chip.on {
    background: ${({ $light }) => ($light ? 'rgba(22,163,74,.14)' : 'rgba(34,197,94,.2)')};
    color: ${({ $light }) => ($light ? '#15803d' : '#86efac')};
    border-color: ${({ $light }) => ($light ? 'rgba(22,163,74,.35)' : 'rgba(34,197,94,.4)')};
  }
  .chip.muted {
    background: ${({ $light }) => ($light ? 'rgba(15,23,42,.06)' : 'rgba(255,255,255,.08)')};
    color: ${({ $light }) => ($light ? '#334155' : '#cbd5e1')};
    border-color: transparent;
  }
  .chip.off {
    background: ${({ $light }) => ($light ? 'rgba(220,38,38,.1)' : 'rgba(239,68,68,.18)')};
    color: ${({ $light }) => ($light ? '#b91c1c' : '#fca5a5')};
    border-color: ${({ $light }) => ($light ? 'rgba(220,38,38,.28)' : 'rgba(239,68,68,.35)')};
  }
  .desc {
    font-size: .84rem;
    color: ${({ $light }) => ($light ? '#475569' : 'rgba(234,242,251,.72)')};
    line-height: 1.45;
    flex: 1;
    padding-left: 6px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .next {
    font-size: .78rem;
    font-weight: 800;
    color: ${({ $light }) => ($light ? '#0f766e' : '#5eead4')};
    padding-left: 6px;
  }
  .actions {
    display: flex; flex-wrap: wrap; gap: 8px; margin-top: auto;
    padding-left: 6px;
    padding-top: 4px;
  }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 10px;
  padding: 9px 12px;
  font-weight: 800;
  font-size: .8rem;
  cursor: pointer;
  transition: filter .12s ease, transform .12s ease;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const EmptyGallery = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 48px 20px;
  border-radius: 16px;
  border: 1px dashed ${({ $light }) => ($light ? 'rgba(23,35,59,.2)' : 'rgba(255,255,255,.2)')};
  background: ${({ $light }) => ($light ? 'rgba(255,255,255,.7)' : 'rgba(255,255,255,.03)')};
  color: ${({ $light }) => ($light ? '#334155' : 'inherit')};
  b { display: block; font-size: 1.1rem; margin-bottom: 8px; color: ${({ $light }) => ($light ? '#0f172a' : 'inherit')}; }
`;

const emptyForm = (meId, members) => ({
  title: '',
  description: '',
  timeHint: '',
  durationMinutes: '',
  assigneeId: String(meId || members?.[0]?.user_id || ''),
  schedule_recurrence: 'weekdays',
  schedule_weekday: 1,
  schedule_weekdays: [1],
  schedule_interval_days: '10',
  schedule_month_days: [],
  schedule_time: '09:00',
  scheduled_at_date: '',
  scheduled_at_time: '09:00',
  enabled: true,
});

export default function KanbanTemplatesPanel({
  light = false,
  members = [],
  me = null,
  canSeeAll = false,
  boardId = null,
  onClose,
  toast = () => {},
}) {
  const meId = me?.userId;
  const [tab, setTab] = useState('create'); // create | list
  const [scope, setScope] = useState('mine');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(meId, members));
  const [cal, setCal] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });
  const [daysMap, setDaysMap] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [shiftTplId, setShiftTplId] = useState(null);
  const [shiftFromDay, setShiftFromDay] = useState(null);

  const cells = useMemo(() => buildMonthCells(cal.y, cal.m), [cal.y, cal.m]);
  const range = useMemo(() => {
    const from = toYmd(new Date(cal.y, cal.m, 1));
    const to = toYmd(new Date(cal.y, cal.m + 1, 0));
    return { from, to };
  }, [cal.y, cal.m]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ scope: canSeeAll && scope === 'all' ? 'all' : 'mine' });
      if (boardId) q.set('boardId', String(boardId));
      const r = await fetch(`/api/kanban/templates?${q}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить шаблоны');
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (e) {
      toast(e.message, 'red');
    } finally {
      setLoading(false);
    }
  }, [canSeeAll, scope, boardId, toast]);

  const loadCalendar = useCallback(async () => {
    try {
      const q = new URLSearchParams({
        from: range.from,
        to: range.to,
        scope: canSeeAll && scope === 'all' ? 'all' : 'mine',
      });
      if (boardId) q.set('boardId', String(boardId));
      const r = await fetch(`/api/kanban/templates/calendar?${q}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Календарь недоступен');
      setDaysMap(data.days && typeof data.days === 'object' ? data.days : {});
    } catch (e) {
      toast(e.message, 'red');
    }
  }, [range.from, range.to, canSeeAll, scope, boardId, toast]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const fillFromTemplate = (tpl) => {
    setEditId(tpl.id);
    let date = '';
    let time = tpl.schedule_time || '09:00';
    if (tpl.scheduled_at) {
      const d = new Date(tpl.scheduled_at);
      if (!Number.isNaN(d.getTime())) {
        date = toYmd(d);
        time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    }
    const weekdays = parseDaysList(tpl.schedule_weekdays);
    if (!weekdays.length && tpl.schedule_weekday != null) weekdays.push(Number(tpl.schedule_weekday));
    setForm({
      title: tpl.title || '',
      description: tpl.description || '',
      timeHint: tpl.time_hint || '',
      durationMinutes: tpl.duration_minutes ? String(tpl.duration_minutes) : '',
      assigneeId: String(tpl.assignee_id || ''),
      schedule_recurrence: tpl.schedule_recurrence || 'none',
      schedule_weekday: tpl.schedule_weekday != null ? Number(tpl.schedule_weekday) : 1,
      schedule_weekdays: weekdays.length ? weekdays : [1],
      schedule_interval_days: tpl.schedule_interval_days ? String(tpl.schedule_interval_days) : '10',
      schedule_month_days: parseDaysList(tpl.schedule_month_days),
      schedule_time: tpl.schedule_time || time || '09:00',
      scheduled_at_date: date,
      scheduled_at_time: time,
      enabled: !!tpl.enabled,
    });
    setTab('create');
  };

  const resetForm = () => {
    setEditId(null);
    setForm(emptyForm(meId, members));
  };

  const buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      description: form.description,
      timeHint: form.timeHint || null,
      durationMinutes: Number(form.durationMinutes) > 0 ? Number(form.durationMinutes) : null,
      assigneeId: Number(form.assigneeId) || meId,
      enabled: !!form.enabled,
      schedule_recurrence: form.schedule_recurrence,
    };
    if (form.schedule_recurrence === 'once') {
      if (!form.scheduled_at_date) throw new Error('Выберите дату в календаре или поле даты');
      const t = form.scheduled_at_time || '09:00';
      payload.scheduled_at = new Date(`${form.scheduled_at_date}T${t}:00`).toISOString();
    } else if (form.schedule_recurrence === 'interval') {
      const n = Number(form.schedule_interval_days);
      if (!Number.isFinite(n) || n < 1) throw new Error('Укажите интервал: каждые N дней');
      payload.schedule_interval_days = Math.round(n);
      payload.schedule_time = form.schedule_time || '09:00';
      if (!form.scheduled_at_date) throw new Error('Выберите первую дату в календаре (когда сделать сейчас / старт)');
      const t = form.scheduled_at_time || form.schedule_time || '09:00';
      payload.scheduled_at = new Date(`${form.scheduled_at_date}T${t}:00`).toISOString();
    } else if (form.schedule_recurrence === 'weekly') {
      const days = Array.isArray(form.schedule_weekdays) && form.schedule_weekdays.length
        ? form.schedule_weekdays.map(Number)
        : [Number(form.schedule_weekday)];
      if (!days.length) throw new Error('Выберите хотя бы один день недели');
      payload.schedule_weekdays = days;
      payload.schedule_weekday = days[0];
      payload.schedule_time = form.schedule_time || '09:00';
    } else if (form.schedule_recurrence === 'monthdays') {
      const days = Array.isArray(form.schedule_month_days) ? form.schedule_month_days.map(Number).filter((n) => n >= 1 && n <= 31) : [];
      if (!days.length) throw new Error('Выберите хотя бы одно число месяца (1–31)');
      payload.schedule_month_days = days;
      payload.schedule_time = form.schedule_time || '09:00';
    } else if (['daily', 'weekdays', 'weekends'].includes(form.schedule_recurrence)) {
      payload.schedule_time = form.schedule_time || '09:00';
    }
    return payload;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (!payload.title) throw new Error('Укажите название шаблона');
      if (!editId && boardId) payload.boardId = boardId;
      const url = editId ? `/api/kanban/templates/${editId}` : '/api/kanban/templates';
      const method = editId ? 'PATCH' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось сохранить');
      toast(editId ? 'Шаблон обновлён' : 'Шаблон создан', 'blue');
      resetForm();
      await loadTemplates();
      await loadCalendar();
      setTab('list');
    } catch (e) {
      toast(e.message, 'red');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Удалить шаблон?')) return;
    try {
      const r = await fetch(`/api/kanban/templates/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось удалить');
      if (editId === id) resetForm();
      toast('Шаблон удалён', 'blue');
      await loadTemplates();
      await loadCalendar();
    } catch (e) {
      toast(e.message, 'red');
    }
  };

  const toggleEnabled = async (tpl) => {
    try {
      const r = await fetch(`/api/kanban/templates/${tpl.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: !tpl.enabled }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка');
      await loadTemplates();
      await loadCalendar();
    } catch (e) {
      toast(e.message, 'red');
    }
  };

  const spawnNow = async (id) => {
    try {
      const r = await fetch(`/api/kanban/templates/${id}/spawn`, { method: 'POST', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось создать задачу');
      toast('Задача появилась в колонке «Новые»', 'green');
    } catch (e) {
      toast(e.message, 'red');
    }
  };

  const onPickDay = (d, e) => {
    if (!d) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const ymd = toYmd(d);
    setSelectedDay(ymd);
    if (shiftTplId) {
      rescheduleTo(shiftTplId, ymd);
      return;
    }

    const rec = form.schedule_recurrence;

    // «Один раз» и «Каждые N дней»: клик по большому календарю → дата в поле справа
    if (rec === 'once' || rec === 'interval') {
      setForm((f) => ({ ...f, scheduled_at_date: ymd }));
      return;
    }

    // «Несколько дней месяца»: клик выделяет/снимает число месяца
    if (rec === 'monthdays') {
      const dayNum = d.getDate();
      setForm((f) => {
        const cur = Array.isArray(f.schedule_month_days) ? f.schedule_month_days.map(Number) : [];
        const has = cur.includes(dayNum);
        const next = has ? cur.filter((n) => n !== dayNum) : [...cur, dayNum].sort((a, b) => a - b);
        return { ...f, schedule_month_days: next };
      });
      return;
    }

    // Остальные режимы: только подсветка дня и просмотр, что уже запланировано
  };

  const toggleWeekday = (dayValue) => {
    setForm((f) => {
      const cur = Array.isArray(f.schedule_weekdays) && f.schedule_weekdays.length
        ? f.schedule_weekdays.map(Number)
        : [Number(f.schedule_weekday) || 1];
      const has = cur.includes(dayValue);
      let next = has ? cur.filter((n) => n !== dayValue) : [...cur, dayValue];
      if (!next.length) next = [dayValue];
      next.sort((a, b) => a - b);
      return { ...f, schedule_weekdays: next, schedule_weekday: next[0] };
    });
  };

  const toggleMonthDay = (dayNum) => {
    setForm((f) => {
      const cur = Array.isArray(f.schedule_month_days) ? f.schedule_month_days.map(Number) : [];
      const has = cur.includes(dayNum);
      const next = has ? cur.filter((n) => n !== dayNum) : [...cur, dayNum].sort((a, b) => a - b);
      return { ...f, schedule_month_days: next };
    });
  };

  const rescheduleTo = async (tplId, ymd) => {
    try {
      const tpl = templates.find((t) => Number(t.id) === Number(tplId));
      const time = tpl?.schedule_time || form.schedule_time || '09:00';
      const r = await fetch(`/api/kanban/templates/${tplId}/reschedule`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ date: ymd, from: shiftFromDay || undefined, time }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось перенести');
      setShiftTplId(null);
      setShiftFromDay(null);
      toast(`Перенесено на ${ymd}`, 'blue');
      await loadTemplates();
      await loadCalendar();
      setSelectedDay(ymd);
    } catch (e) {
      toast(e.message, 'red');
    }
  };

  const todayYmd = toYmd(new Date());
  const dayItems = selectedDay && daysMap[selectedDay] ? daysMap[selectedDay] : [];

  return (
    <Overlay onClick={onClose}>
      <Panel $light={light} onClick={(e) => e.stopPropagation()}>
        <Head $light={light}>
          <FiCalendar size={18} color="#38b6ff" />
          <h3>Шаблоны задач</h3>
          {canSeeAll && (
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              style={{
                borderRadius: 8, padding: '6px 8px', fontWeight: 700,
                border: '1px solid rgba(56,182,255,.35)', background: 'transparent', color: 'inherit',
              }}
            >
              <option value="mine">Мои шаблоны</option>
              <option value="all">Все участники</option>
            </select>
          )}
          <button type="button" className="close" onClick={onClose} aria-label="Закрыть"><FiX size={18} /></button>
        </Head>

        <Tabs>
          <TabBtn type="button" $light={light} $on={tab === 'create'} onClick={() => setTab('create')}>
            <FiPlus size={16} />
            {editId ? 'Редактирование' : 'Создать шаблон'}
          </TabBtn>
          <TabBtn type="button" $light={light} $on={tab === 'list'} onClick={() => setTab('list')}>
            <FiList size={16} />
            Шаблоны
            <span className="badge">{templates.length}</span>
          </TabBtn>
        </Tabs>

        <ScrollBody>
          {tab === 'create' ? (
            <CreateLayout>
              <Card $light={light}>
                <CalWrap>
                  <CalHead>
                    <button type="button" onClick={() => setCal((c) => ({ y: c.m === 0 ? c.y - 1 : c.y, m: c.m === 0 ? 11 : c.m - 1 }))}>
                      <FiChevronLeft />
                    </button>
                    <span>{monthLabel(cal.y, cal.m)}</span>
                    <button type="button" onClick={() => setCal((c) => ({ y: c.m === 11 ? c.y + 1 : c.y, m: c.m === 11 ? 0 : c.m + 1 }))}>
                      <FiChevronRight />
                    </button>
                  </CalHead>
                  <Dow>
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => <span key={d}>{d}</span>)}
                  </Dow>
                  <Grid>
                    {cells.map((d, i) => {
                      if (!d) return <DayCell key={`e${i}`} disabled $light={light} />;
                      const ymd = toYmd(d);
                      const busy = Array.isArray(daysMap[ymd]) && daysMap[ymd].length > 0;
                      const dateMode = form.schedule_recurrence === 'once' || form.schedule_recurrence === 'interval';
                      const monthMode = form.schedule_recurrence === 'monthdays';
                      const datePicked = dateMode && form.scheduled_at_date === ymd;
                      const monthPicked =
                        monthMode &&
                        Array.isArray(form.schedule_month_days) &&
                        form.schedule_month_days.map(Number).includes(d.getDate());
                      return (
                        <DayCell
                          key={ymd}
                          type="button"
                          $light={light}
                          $busy={busy}
                          $today={ymd === todayYmd}
                          $selected={datePicked || (selectedDay === ymd && !monthPicked && !datePicked)}
                          $picked={monthPicked}
                          onClick={(e) => onPickDay(d, e)}
                          title={
                            dateMode
                              ? `Выбрать дату ${ymd}`
                              : monthPicked
                                ? `Снять ${d.getDate()}-е`
                                : monthMode
                                  ? `Выделить ${d.getDate()}-е каждого месяца`
                                  : `День ${ymd}`
                          }
                        >
                          {d.getDate()}
                        </DayCell>
                      );
                    })}
                  </Grid>
                </CalWrap>
                <div style={{
                  marginTop: 8, padding: '8px 10px', borderRadius: 10,
                  background: (form.schedule_recurrence === 'once' || form.schedule_recurrence === 'interval') && form.scheduled_at_date
                    ? 'rgba(56,182,255,.12)'
                    : Array.isArray(form.schedule_month_days) && form.schedule_month_days.length
                      ? 'rgba(251,191,36,.14)'
                      : (light ? 'rgba(23,35,59,.04)' : 'rgba(255,255,255,.04)'),
                  border: `1px solid ${(form.schedule_recurrence === 'once' || form.schedule_recurrence === 'interval') ? 'rgba(56,182,255,.4)' : 'rgba(251,191,36,.35)'}`,
                  fontSize: '.82rem', fontWeight: 700, lineHeight: 1.45,
                }}>
                  {form.schedule_recurrence === 'once' && (
                    form.scheduled_at_date
                      ? `Выбрана дата: ${form.scheduled_at_date}. Она подставилась в поле справа. Можно кликнуть другой день.`
                      : 'Режим «Один раз»: кликните день на этом календаре — дата появится в поле справа.'
                  )}
                  {form.schedule_recurrence === 'interval' && (
                    form.scheduled_at_date
                      ? `Стартовая дата: ${form.scheduled_at_date}. Она в поле справа.`
                      : 'Режим «Каждые N дней»: кликните день на календаре — это будет первая дата запуска.'
                  )}
                  {form.schedule_recurrence === 'monthdays' && (
                    Array.isArray(form.schedule_month_days) && form.schedule_month_days.length
                      ? `Выбрано: ${form.schedule_month_days.join(', ')}-е каждого месяца. Повторный клик снимает день.`
                      : 'Режим «Несколько дней месяца»: кликайте числа — они будут повторяться каждый месяц.'
                  )}
                  {!['once', 'interval', 'monthdays'].includes(form.schedule_recurrence) && (
                    'Календарь показывает уже запланированные шаблоны (точка под днём). Клик — посмотреть детали.'
                  )}
                </div>
                {shiftTplId && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 10,
                    background: 'rgba(251,191,36,.15)', border: '1px solid rgba(251,191,36,.4)',
                    fontSize: '.82rem', fontWeight: 700,
                  }}>
                    Режим переноса{shiftFromDay ? ` с ${shiftFromDay}` : ''}: кликните новую дату
                    <button type="button" style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() => { setShiftTplId(null); setShiftFromDay(null); }}>Отмена</button>
                  </div>
                )}
                {selectedDay && (
                  <DayDetail $light={light}>
                    <b>{selectedDay}</b>
                    {dayItems.length === 0 ? (
                      <div style={{ opacity: 0.65, marginTop: 6 }}>
                        Нет запланированных шаблонов на этот день.
                      </div>
                    ) : (
                      <ul>
                        {dayItems.map((it) => (
                          <li key={`${it.id}-${it.title}`}>
                            <b>{it.title}</b>
                            {' · '}
                            {recLabel(it.schedule_recurrence, it.schedule_interval_days, it.schedule_month_days, it.schedule_weekdays)}
                            {it.schedule_time ? ` в ${it.schedule_time}` : ''}
                            <button
                              type="button"
                              style={{
                                marginLeft: 8, cursor: 'pointer', fontWeight: 800,
                                border: '1px solid rgba(56,182,255,.45)', borderRadius: 8,
                                background: 'rgba(56,182,255,.15)', color: '#38b6ff', padding: '2px 8px',
                              }}
                              onClick={() => {
                                setShiftTplId(it.id);
                                setShiftFromDay(selectedDay);
                              }}
                            >
                              Перенести
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </DayDetail>
                )}
              </Card>

              <Card $light={light}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                  <b style={{ fontSize: '1rem' }}>{editId ? 'Редактирование шаблона' : 'Новый шаблон'}</b>
                  {editId && (
                    <Btn $ghost type="button" onClick={resetForm}><FiPlus /> Новый</Btn>
                  )}
                </div>
                <Field $light={light}>
                  Название задачи
                  <input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Например: Сверка кассы" />
                </Field>
                <Field $light={light}>
                  Описание
                  <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="Необязательно" />
                </Field>
                <Field $light={light}>
                  Исполнитель
                  <select
                    value={canSeeAll ? form.assigneeId : String(meId || form.assigneeId || '')}
                    onChange={(e) => setField('assigneeId', e.target.value)}
                    disabled={!canSeeAll}
                  >
                    {(canSeeAll ? members : members.filter((m) => Number(m.user_id) === Number(meId))).map((m) => (
                      <option key={m.user_id} value={m.user_id}>{displayName(m)}</option>
                    ))}
                  </select>
                </Field>
                <Field $light={light}>
                  Подсказка времени на карточке (необязательно)
                  <input value={form.timeHint} onChange={(e) => setField('timeHint', e.target.value)} placeholder="например 18:00" />
                </Field>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '.78rem', fontWeight: 700, opacity: 0.75, marginBottom: 6 }}>
                    Лимит на выполнение — таймер с момента «В работу»
                  </div>
                  <DurationPicker
                    light={light}
                    value={form.durationMinutes}
                    onChange={(v) => setField('durationMinutes', v)}
                    placeholder="Без таймера · нажмите, чтобы выбрать"
                  />
                </div>

                <div style={{ fontSize: '.78rem', fontWeight: 700, opacity: 0.75, marginBottom: 6 }}>Повтор / автозапуск в «Новые»</div>
                <RecRow $light={light}>
                  {REC_OPTIONS.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      className={form.schedule_recurrence === o.v ? 'on' : ''}
                      onClick={() => setField('schedule_recurrence', o.v)}
                    >
                      {o.label}
                    </button>
                  ))}
                </RecRow>

                {form.schedule_recurrence === 'weekly' && (
                  <>
                    <RecRow $light={light}>
                      {WEEKDAYS.map((d) => (
                        <button
                          key={d.v}
                          type="button"
                          className={(Array.isArray(form.schedule_weekdays) ? form.schedule_weekdays : [form.schedule_weekday]).map(Number).includes(d.v) ? 'on' : ''}
                          onClick={() => toggleWeekday(d.v)}
                        >
                          {d.label}
                        </button>
                      ))}
                    </RecRow>
                    <p style={{ margin: '0 0 10px', fontSize: '.75rem', opacity: 0.65, lineHeight: 1.4 }}>
                      Можно выбрать несколько дней недели (например Пн, Ср, Пт).
                    </p>
                  </>
                )}

                {form.schedule_recurrence === 'monthdays' && (
                  <>
                    <RecRow $light={light}>
                      {MONTH_DAY_NUMS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={(Array.isArray(form.schedule_month_days) ? form.schedule_month_days : []).map(Number).includes(n) ? 'on' : ''}
                          onClick={() => toggleMonthDay(n)}
                        >
                          {n}
                        </button>
                      ))}
                    </RecRow>
                    <p style={{ margin: '0 0 10px', fontSize: '.75rem', opacity: 0.65, lineHeight: 1.4 }}>
                      Выберите числа в календаре слева или кнопками выше.
                    </p>
                  </>
                )}

                {form.schedule_recurrence === 'interval' && (
                  <>
                    <Field $light={light}>
                      Интервал (дней)
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={form.schedule_interval_days}
                        onChange={(e) => setField('schedule_interval_days', e.target.value)}
                        placeholder="10"
                      />
                    </Field>
                    <Field $light={light}>
                      Первая дата (выбирается на календаре слева)
                      <input type="date" value={form.scheduled_at_date} onChange={(e) => setField('scheduled_at_date', e.target.value)} />
                    </Field>
                  </>
                )}

                {['daily', 'weekdays', 'weekends', 'weekly', 'interval', 'monthdays'].includes(form.schedule_recurrence) && (
                  <Field $light={light}>
                    Время запуска <FiClock style={{ display: 'inline' }} />
                    <input type="time" value={form.schedule_time} onChange={(e) => setField('schedule_time', e.target.value)} />
                  </Field>
                )}

                {form.schedule_recurrence === 'once' && (
                  <>
                    <Field $light={light}>
                      Дата запуска (выбирается на календаре слева)
                      <input type="date" value={form.scheduled_at_date} onChange={(e) => setField('scheduled_at_date', e.target.value)} />
                    </Field>
                    <Field $light={light}>
                      Время
                      <input type="time" value={form.scheduled_at_time} onChange={(e) => setField('scheduled_at_time', e.target.value)} />
                    </Field>
                    {!form.scheduled_at_date && (
                      <p style={{ margin: '0 0 10px', fontSize: '.78rem', fontWeight: 700, color: '#38b6ff', lineHeight: 1.4 }}>
                        Кликните день на большом календаре слева — дата появится здесь.
                      </p>
                    )}
                  </>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
                  Автозапуск включён
                </label>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Btn type="button" onClick={save} disabled={saving}>
                    <FiCheck /> {editId ? 'Сохранить' : 'Создать шаблон'}
                  </Btn>
                  {editId && (
                    <Btn $ghost type="button" onClick={resetForm}>Сбросить</Btn>
                  )}
                </div>
                <p style={{ margin: '12px 0 0', fontSize: '.78rem', opacity: 0.65, lineHeight: 1.45 }}>
                  После создания шаблон появится во вкладке «Шаблоны». По расписанию задача попадёт в колонку «Новые».
                </p>
              </Card>
            </CreateLayout>
          ) : (
            <>
              <div style={{ marginBottom: 14 }}>
                <b style={{ fontSize: '1.05rem' }}>Готовые шаблоны</b>
                <div style={{ fontSize: '.8rem', opacity: 0.6, marginTop: 2 }}>
                  {loading ? 'Загрузка…' : templates.length ? `${templates.length} шт.` : 'Пока пусто'}
                </div>
              </div>
              <Gallery>
                {!loading && !templates.length && (
                  <EmptyGallery $light={light}>
                    <b>Пока нет шаблонов</b>
                    Перейдите во вкладку «Создать шаблон», чтобы добавить первый.
                  </EmptyGallery>
                )}
                {templates.map((tpl) => (
                  <TplCard key={tpl.id} $light={light} $on={editId === tpl.id} $enabled={!!tpl.enabled}>
                    <h4 className="title">{tpl.title}</h4>
                    <div className="meta">
                      <span className={`chip ${tpl.enabled ? 'on' : 'off'}`}>
                        {tpl.enabled ? '● Включён' : '○ Выключен'}
                      </span>
                      <span className="chip">
                        {recLabel(tpl.schedule_recurrence, tpl.schedule_interval_days, tpl.schedule_month_days, tpl.schedule_weekdays)}
                        {tpl.schedule_time ? ` · ${tpl.schedule_time}` : ''}
                      </span>
                      {Number(tpl.duration_minutes) > 0 && (
                        <span className="chip muted">⏱ {formatDurationLabel(tpl.duration_minutes)}</span>
                      )}
                      <span className="chip muted">→ {displayName(tpl)}</span>
                    </div>
                    {tpl.description ? <div className="desc">{tpl.description}</div> : <div className="desc" style={{ opacity: 0.45 }}>Без описания</div>}
                    {tpl.scheduled_at && (tpl.schedule_recurrence === 'once' || tpl.schedule_recurrence === 'interval') && (
                      <div className="next">
                        След.: {new Date(tpl.scheduled_at).toLocaleString('ru-RU')}
                      </div>
                    )}
                    <div className="actions">
                      <ActionBtn
                        type="button"
                        onClick={() => fillFromTemplate(tpl)}
                        style={{
                          background: light ? 'rgba(15,23,42,.07)' : 'rgba(255,255,255,.1)',
                          color: light ? '#0f172a' : '#e2e8f0',
                        }}
                        title="Открыть в редакторе"
                      >
                        <FiEdit2 /> Изменить
                      </ActionBtn>
                      <ActionBtn
                        type="button"
                        onClick={() => toggleEnabled(tpl)}
                        style={tpl.enabled
                          ? { background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', boxShadow: '0 4px 12px rgba(22,163,74,.35)' }
                          : { background: light ? 'rgba(100,116,139,.18)' : 'rgba(148,163,184,.25)', color: light ? '#334155' : '#e2e8f0' }}
                        title={tpl.enabled ? 'Выключить автозапуск' : 'Включить автозапуск'}
                      >
                        {tpl.enabled ? <FiToggleRight /> : <FiToggleLeft />}
                        {tpl.enabled ? 'Вкл.' : 'Выкл.'}
                      </ActionBtn>
                      <ActionBtn
                        type="button"
                        onClick={() => spawnNow(tpl.id)}
                        style={{
                          background: 'linear-gradient(135deg,#38b6ff,#2488d8)',
                          color: '#fff',
                          boxShadow: '0 4px 12px rgba(56,182,255,.35)',
                        }}
                        title="Сразу создать задачу из шаблона в колонку «Новые»"
                      >
                        <FiPlay /> Сейчас
                      </ActionBtn>
                      <ActionBtn
                        type="button"
                        onClick={() => remove(tpl.id)}
                        style={{
                          background: 'linear-gradient(135deg,#ef4444,#b91c1c)',
                          color: '#fff',
                          boxShadow: '0 4px 12px rgba(239,68,68,.35)',
                          minWidth: 42,
                          justifyContent: 'center',
                        }}
                        title="Удалить шаблон"
                      >
                        <FiTrash2 size={16} />
                      </ActionBtn>
                    </div>
                  </TplCard>
                ))}
              </Gallery>
            </>
          )}
        </ScrollBody>
      </Panel>
    </Overlay>
  );
}
